/**
 * Акции всех брендов.
 *
 * Отсюда оператор может завести акцию за компанию и опубликовать её. Актором
 * в журнале при этом остаётся сотрудник платформы, а не бренд: подменять
 * автора решения нельзя даже ради красоты ленты событий.
 */
import type { Brand, Campaign } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import {
  Badge, Button, Card, Empty, Icon, Segmented, Skeleton, fmtShortDay, num,
} from '@allswipes/ui';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

const STATUS_LABEL: Record<string, string> = {
  draft: 'черновик',
  scheduled: 'запланирована',
  active: 'идёт',
  ended: 'завершена',
  drawn: 'розыгрыш проведён',
};

export function CampaignsScreen() {
  const [params, setParams] = useSearchParams();
  const brand = params.get('brand') ?? '';
  const status = params.get('status') ?? '';
  const data = useResource(() => api.adminCampaigns({ brand, status }), [brand, status]);
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState<Campaign | null>(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'all' | 'demo' | 'live'>('all');
  const [where, setWhere] = useState<'feed' | 'archive' | 'all'>('feed');

  const brands = data.data?.brands ?? [];
  const byId = useMemo(() => {
    const map = new Map<string, Brand>();
    for (const b of brands) map.set(b.id, b);
    return map;
  }, [brands]);

  const all = data.data?.campaigns ?? [];
  const demoCount = all.filter((c) => c.demo).length;
  const archivedCount = all.filter((c) => c.archived_at).length;
  const found = all
    .filter((c) =>
      where === 'all' ? true : where === 'archive' ? !!c.archived_at : !c.archived_at,
    )
    .filter((c) => (kind === 'all' ? true : kind === 'demo' ? c.demo : !c.demo))
    .filter((c) =>
      `${c.name} ${c.recipe} ${byId.get(c.brand_id)?.name ?? ''}`
        .toLowerCase()
        .includes(q.trim().toLowerCase()),
    );

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const act = async (fn: Promise<unknown>, done: string) => {
    try {
      await fn;
      toast(done);
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <Layout
      title="Акции"
      subtitle="все бренды платформы"
      /* «Создать за бренд» на странице «Акции» читалось как «создать бренд»:
         слово «бренд» стоит сразу за «создать», и глаз склеивает их в
         название кнопки. Главное действие страницы называет то, что оно
         создаёт; за кого — уточнение, и его место в заголовке окна. */
      action={
        canModerate ? (
          <Button variant="pri" icon="plus" onClick={() => setCreating(true)}>
            Создать акцию
          </Button>
        ) : undefined
      }
    >
      <ReadOnlyNote />

      <div className="note" style={{ marginBottom: 14 }}>
        <Icon name="info" size={14} />
        <span>
          Демо-акция видна в ленте и выглядит как настоящая, но участия сервер не проводит: приза
          за ней нет, и выдавать его некому. Покупателю это не показывается — отказ выглядит как
          «такого кода не существует».
        </span>
      </div>

      <div className="row" style={{ gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <select className="select" value={brand} onChange={(e) => setFilter('brand', e.target.value)}>
          <option value="">Все бренды</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select className="select" value={status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">Любой статус</option>
          {Object.entries(STATUS_LABEL).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="Поиск по названию, бренду или механике"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        {/* Витрина и настоящие акции лежат в одном списке — разделяет их
            только этот переключатель: платформа обязана видеть и те, и
            другие разом, иначе демо-акция однажды уедет в бой незамеченной. */}
        {/* Архив — это ответ на вопрос «показывать ли в ленте», а не статус.
            Поэтому отдельный переключатель рядом со статусом, а не внутри. */}
        <Segmented
          value={where}
          options={[
            { value: 'feed', label: `В ленте ${all.length - archivedCount}` },
            { value: 'archive', label: `В архиве ${archivedCount}` },
            { value: 'all', label: 'Везде' },
          ]}
          onChange={setWhere}
        />
        <Segmented
          value={kind}
          options={[
            { value: 'all', label: `Все ${all.length}` },
            { value: 'demo', label: `Витрина ${demoCount}` },
            { value: 'live', label: `Живые ${all.length - demoCount}` },
          ]}
          onChange={setKind}
        />
      </div>

      {data.loading && <Skeleton count={4} height={52} />}
      {data.data && found.length === 0 && <Empty icon="list" title="Ничего не нашлось" />}

      {found.length > 0 && (
        <Card>
          <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>Акция</th>
                <th>Бренд</th>
                <th>Механика</th>
                <th>Участников</th>
                <th>Срок</th>
                <th>Статус</th>
                <th>Демо</th>
                <th>Карточка</th>
                <th>Призы</th>
                {canModerate && <th />}
              </tr>
            </thead>
            <tbody>
              {found.map((c: Campaign) => (
                <tr key={c.id}>
                  <td>
                    {c.name}
                    {c.archived_at && (
                      <div className="faint" style={{ fontSize: 11 }}>
                        в архиве
                      </div>
                    )}
                  </td>
                  <td className="faint">{byId.get(c.brand_id)?.name ?? '—'}</td>
                  <td className="mono">{c.recipe}</td>
                  <td>{num(c.participants)}</td>
                  <td className="faint">{fmtShortDay(c.end_at)}</td>
                  <td>
                    <Badge tone={c.status === 'active' ? 'live' : undefined}>
                      {STATUS_LABEL[c.status] ?? c.status}
                    </Badge>
                  </td>
                  <td>
                    {canModerate ? (
                      <button
                        type="button"
                        className={`chip ${c.demo ? 'on' : ''}`}
                        title={
                          c.demo
                            ? 'Витрина: участие сервер не проводит, покупателю это не видно'
                            : 'Настоящая акция: коды принимаются'
                        }
                        onClick={() =>
                          void act(
                            api.adminSetCampaignFlags(c.id, { demo: !c.demo }),
                            c.demo ? 'Акция стала настоящей' : 'Акция помечена демонстрационной',
                          )
                        }
                      >
                        {c.demo ? 'демо' : 'живая'}
                      </button>
                    ) : (
                      <span className="faint">{c.demo ? 'демо' : 'живая'}</span>
                    )}
                  </td>
                  <td>
                    {/* Скрытая карточка: в ленте остаётся название, а приз и
                        условия открывает ссылка канала. Это исключение из
                        правила «условия видны всем до участия», поэтому
                        включает его платформа, а не партнёр сам. */}
                    {canModerate ? (
                      <button
                        type="button"
                        className={`chip ${c.secret_details ? 'on' : ''}`}
                        onClick={() =>
                          void act(
                            api.adminSetCampaignFlags(c.id, { secret_details: !c.secret_details }),
                            c.secret_details
                              ? 'Карточка снова открыта'
                              : 'Содержимое скрыто — его откроет ссылка',
                          )
                        }
                      >
                        {c.secret_details ? 'по ссылке' : 'открыта'}
                      </button>
                    ) : (
                      <span className="faint">{c.secret_details ? 'по ссылке' : 'открыта'}</span>
                    )}
                  </td>
                  <td>
                    {c.prizes_verified_at ? (
                      <button
                        type="button"
                        className="chip on"
                        title="Отметка стоит. Нажми, чтобы снять."
                        disabled={!canModerate}
                        onClick={() =>
                          void act(
                            api.adminSetCampaignFlags(c.id, { prizes_verified: false }),
                            'Отметка снята',
                          )
                        }
                      >
                        проверены
                      </button>
                    ) : c.prize_proof_url ? (
                      <Button size="sm" disabled={!canModerate} onClick={() => setChecking(c)}>
                        Проверить
                      </Button>
                    ) : (
                      <span className="faint" style={{ fontSize: 12 }}>
                        нет доказательства
                      </span>
                    )}
                  </td>
                  {canModerate && (
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {c.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => void act(api.adminPublish(c.id), 'Акция запущена')}
                          >
                            Запустить
                          </Button>
                        )}
                        {c.status === 'active' && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() =>
                              void act(api.adminCloseCampaign(c.id), 'Приём кодов закрыт')
                            }
                          >
                            Закрыть приём
                          </Button>
                        )}
                        {/* Идущую акцию убрать нельзя: человек с кодом в руках
                            должен найти её там, где она была. Поэтому кнопка
                            появляется только у завершённых. */}
                        {(c.status === 'ended' || c.status === 'drawn') && (
                          <Button
                            size="sm"
                            onClick={() =>
                              void act(
                                api.adminArchiveCampaign(c.id, !c.archived_at),
                                c.archived_at ? 'Акция вернулась в ленту' : 'Акция убрана в архив',
                              )
                            }
                          >
                            {c.archived_at ? 'Вернуть в ленту' : 'В архив'}
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {checking && (
        <ProofDialog
          campaign={checking}
          onClose={() => setChecking(null)}
          onDone={() => {
            setChecking(null);
            data.reload();
          }}
        />
      )}

      {creating && (
        <CreateDialog
          brands={brands}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

/**
 * Проверка призового фонда.
 *
 * Отметка «призы проверены» означает, что живой человек посмотрел на
 * доказательство и отвечает за это именем: в журнале остаётся кто и когда.
 * Поэтому ставится она здесь, рядом с самим доказательством, а не кнопкой в
 * списке — чтобы нельзя было отметить, не посмотрев.
 */
function ProofDialog({
  campaign,
  onClose,
  onDone,
}: {
  campaign: Campaign;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const proof = campaign.prize_proof_url ?? '';
  const isImage = /\.(jpe?g|png|webp)(\?|$)/i.test(proof);

  const verify = async () => {
    setBusy(true);
    try {
      await api.adminSetCampaignFlags(campaign.id, { prizes_verified: true });
      toast('Отметка поставлена — покупатель увидит её на карточке');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="h2">Призы по акции «{campaign.name}»</div>
        <p className="sub" style={{ margin: '6px 0 12px' }}>
          Отметка говорит покупателю одно: платформа посмотрела доказательство и подтверждает, что
          призы существуют. Это не гарантия выдачи — за неё отвечает организатор. В журнале
          останется, кто поставил отметку.
        </p>

        <div className="krow">
          <span className="k">Приз</span>
          <span className="v">{campaign.prize_title || '—'}</span>
        </div>
        {campaign.prize_proof_note && (
          <p className="sub" style={{ marginTop: 10 }}>
            {campaign.prize_proof_note}
          </p>
        )}

        {isImage ? (
          <img
            src={proof}
            alt="Доказательство призового фонда"
            style={{
              marginTop: 12,
              width: '100%',
              maxHeight: 320,
              objectFit: 'contain',
              borderRadius: 10,
              border: '1px solid var(--border)',
              background: 'var(--surface-2)',
            }}
          />
        ) : (
          <a
            className="btn out sm"
            href={proof}
            target="_blank"
            rel="noreferrer"
            style={{ marginTop: 12 }}
          >
            Открыть доказательство
          </a>
        )}

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Закрыть</Button>
          <span className="spacer" />
          <Button variant="pri" disabled={busy} onClick={verify}>
            Призы проверены
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Черновик акции за бренд.
 *
 * Здесь только розыгрыш по кодам: это единственная механика, у которой не
 * нужен отдельный редактор призов. Всё остальное — в кабинете партнёра, и
 * дублировать его мастер в админке значило бы разойтись с ним на второй же
 * правке.
 */
function CreateDialog({
  brands,
  onClose,
  onDone,
}: {
  brands: Brand[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [brandId, setBrandId] = useState(brands[0]?.id ?? '');
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [prize, setPrize] = useState('');
  const [winners, setWinners] = useState(10);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const brand = brands.find((b) => b.id === brandId);

  const submit = async () => {
    if (!brand) return;
    setBusy(true);
    const start = new Date();
    const end = new Date(Date.now() + days * 24 * 3600 * 1000);
    try {
      await api.adminSaveDraft(brand.id, {
        recipe: 'raffle',
        name: name.trim(),
        tagline: tagline.trim(),
        cta_text: 'Участвовать',
        prize: prize.trim(),
        winners,
        pts_per_code: 10,
        code_place: 'в чеке',
        age18: false,
        regions: brand.regions,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        draw_at: new Date(end.getTime() + 24 * 3600 * 1000).toISOString(),
        config: {},
        logo_mode: 'brand',
        logo_url: '',
        pages: [],
        organizer: brand.organizer,
        legal_id: brand.legal_id,
        contact: brand.contact,
        handout_text: 'Приз выдаётся по заявке из приложения.',
        handout_days: 21,
        rules_text: '',
      });
      toast('Черновик создан — запусти его, когда всё проверишь');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="h2">Акция за бренд</div>
        <p className="sub" style={{ margin: '6px 0 14px' }}>
          Создаётся черновик розыгрыша по кодам. Автором в журнале останешься ты, а организатором
          в правилах — бренд.
        </p>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Бренд</span>
            <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="lab">Название</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="lab">Подпись</span>
            <input className="input" value={tagline} onChange={(e) => setTagline(e.target.value)} />
          </label>
          <label className="field">
            <span className="lab">Приз</span>
            <input className="input" value={prize} onChange={(e) => setPrize(e.target.value)} />
          </label>
          <label className="field">
            <span className="lab">Победителей</span>
            <input
              className="input mono"
              type="number"
              min={1}
              value={winners}
              onChange={(e) => setWinners(Number(e.target.value))}
            />
          </label>
          <label className="field">
            <span className="lab">Длительность, дней</span>
            <input
              className="input mono"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button
            variant="pri"
            disabled={busy || !brandId || name.trim().length < 3 || prize.trim().length < 3}
            onClick={submit}
          >
            Создать черновик
          </Button>
        </div>
      </div>
    </div>
  );
}
